import { CoordinatorService } from './coordinator.service';
import { createToken, Lexer, EmbeddedActionsParser, TokenType } from 'chevrotain';
import { Keyword$ } from './keyword$';

type RULE = {
    tag: string;
    class?: string;
    style?: string;
    regexp: RegExp;
    weight: number;
    token?: { name: string; token: TokenType };
};

const defaultRule: Omit<RULE, 'regexp'> = {
    tag: 'span',
    class: '',
    weight: 1,
};

function applyDefaults(rule: Omit<RULE, 'regexp'> & { regexp: RegExp }): RULE {
    return { ...defaultRule, ...rule };
}

// Function to create dynamic tokens
function createDynamicToken(name: string, pattern: RegExp): TokenType {
    return createToken({ name, pattern });
}

export class ScriptXml {
    #coords: CoordinatorService;

    private _parser?: any;
    private _lexer?: Lexer;
    private _ruleLs: RULE[] = [];

    constructor(coords: CoordinatorService) {
        this.#coords = coords;
    }

    addKeywords() {
        const ls: RULE[] = this.#coords.keyword$.keywordLs.map((kw) => ({
            tag: 'span',
            regexp: new RegExp(kw.source, 'i'),
            weight: kw.weight,
            class: kw.groupId,
            style: kw.style,
        }));
        this.addRule(ls);
    }

    addHighlights(type: string) {
        const fLs = this.#coords.highlight$.getField(type);
        if (type == 'title' || type == 'abstract') {
            const textLs = this.#coords.highlight$.getField('text');
            textLs.forEach((t) => {
                if (!fLs.includes(t)) fLs.push(t);
            });
        }
        // console.log('TTTT  TYPE-LS', type, fLs);
        const ls: RULE[] = [];
        fLs.forEach((txt: string) => {
            if (txt.startsWith('/')) txt = txt.slice(1, -1);
            else txt = txt.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            try {
                if (txt) {
                    const rg = new RegExp(txt, 'i');

                    ls.push({
                        tag: 'span',
                        regexp: rg,
                        weight: 1000000,
                        class: '_hit',
                        style: this.#coords.search$.highlightStyle.replaceAll(/\s+/g, ' ').trim(),
                    });
                }
            } catch (error) {}
        });
        ls.length && this.addRule(ls);
    }

    addRule(
        rule:
            | (Omit<RULE, 'regexp'> & { regexp: RegExp })
            | (Omit<RULE, 'regexp'> & { regexp: RegExp })[],
    ) {
        if (Array.isArray(rule)) {
            const rulesWithDefaults = rule.map((r) => applyDefaults(r));
            this._ruleLs.push(...rulesWithDefaults);
        } else {
            this._ruleLs.push(applyDefaults(rule));
        }
        this._ruleLs.sort((a, b) => b.weight - a.weight);
        this._prepareParser();
    }

    get rules(): RULE[] {
        return this._ruleLs;
    }

    get output() {
        return 'XML SCRIPT';
    }

    private _prepareParser() {
        const tokens: TokenType[] = [];

        this._ruleLs.forEach((rule, index) => {
            const name = `Token${index + 1}`;
            const token = createDynamicToken(name, rule.regexp);
            rule.token = { token, name };
            tokens.push(token);
        });

        const Rest = createToken({
            name: 'TokenRest',
            pattern: /.+?/,
        });
        tokens.push(Rest);

        this._lexer = new Lexer(tokens, {
            skipValidations: true,
            deferDefinitionErrorsHandling: true,
        });

        const classLs = this._ruleLs.map((rule) => rule.class || '');
        const styleLs = this._ruleLs.map((rule) => rule.style || '');

        class Parser extends EmbeddedActionsParser {
            result = '';

            constructor() {
                super(tokens);
                const $ = this;

                for (let i = 0; i < tokens.length - 1; i++) {
                    // Exclude the last token 'Rest' here
                    const ruleName = `Rule${i + 1}`;
                    $.RULE(ruleName, () => {
                        const x = $.CONSUME(tokens[i]);
                        const cls = classLs[i];
                        const style = styleLs[i];
                        return `<span class="${cls}" style="${style}">${x.image}</span>`;
                    });
                }

                const rest = $.RULE('RuleRest', () => {
                    const x = $.CONSUME(Rest);
                    return `${x.image}`;
                });

                $.RULE('topRule', () => {
                    let result = '';
                    $.MANY(() => {
                        $.OR([
                            ...tokens.slice(0, -1).map((_, i) => ({
                                ALT: () => {
                                    this.result += $.SUBRULE((this as any)[`Rule${i + 1}`]);
                                },
                            })),
                            {
                                ALT: () => {
                                    this.result += $.SUBRULE(rest);
                                },
                            },
                        ]);
                    });
                });

                this.performSelfAnalysis();
            }
        }

        this._parser = new Parser();
        // console.log('Parser created!', this._parser);
    }

    parse(txt: string) {
        if (!this._parser || !txt) return txt;
        txt = txt.replaceAll(/\s+/g, ' ').trim();
        const parser = this._parser!;
        parser.result = '';
        const lexingResult = this._lexer!.tokenize(txt);
        parser.input = lexingResult.tokens;

        if (parser.errors.length > 0) {
            for (const error of parser.errors) {
                console.log(error.message);
            }
            throw new Error('Parsing errors detected!');
        }

        parser.topRule();
        return parser.result;
    }
}
